/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { speak } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Footprints, Flame, Trophy, Crown, Medal, HeartPulse, Target, Pill, Users, Lock, Volume2, MessageCircle, Utensils } from "lucide-react";
import { toast } from "sonner";

const ICONS = { footprints: Footprints, flame: Flame, trophy: Trophy, crown: Crown, medal: Medal, "heart-pulse": HeartPulse, target: Target, pill: Pill, users: Users, utensils: Utensils };

export function BadgesRow() {
  const { patient, lang, online } = useApp();
  const [data, setData] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bc_badges") || "null"); } catch { return null; }
  });
  const [celebrate, setCelebrate] = useState(null);

  useEffect(() => {
    if (!patient) return;
    api.get(`/patients/${patient.id}/badges`).then(({ data }) => {
      setData(data);
      localStorage.setItem("bc_badges", JSON.stringify({ ...data, nouveaux: [] }));
      if (data.nouveaux.length) setCelebrate(data.nouveaux[0]);
    }).catch(() => null);
  }, [patient, online]);

  const closeCelebrate = async () => {
    setCelebrate(null);
    try { await api.post(`/patients/${patient.id}/badges/seen`); } catch (e) { /* offline */ }
  };

  useEffect(() => {
    if (celebrate) speak(lang === "ln" ? celebrate.ln : `Félicitations ! ${celebrate.texte}`, lang);
  }, [celebrate, lang]);

  const shareBadge = async (b) => {
    const nom = (patient?.nom || "").split(" ")[0];
    const imgUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/badge/${b.code}.png?nom=${encodeURIComponent(nom)}${lang === "ln" ? "&ln=1" : ""}`;
    const text = lang === "ln" ? `${b.ln} — Bombacare Connect` : `J'ai gagné le badge « ${b.titre} » sur Bombacare Connect ! ${b.texte}`;
    if (navigator.share) {
      try {
        const blob = await (await fetch(imgUrl)).blob();
        const file = new File([blob], `bombacare-${b.code}.png`, { type: "image/png" });
        if (navigator.canShare?.({ files: [file] })) { await navigator.share({ title: b.titre, text, files: [file] }); return; }
        await navigator.share({ title: b.titre, text, url: imgUrl }); return;
      } catch (e) { if (e?.name === "AbortError") return; }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${imgUrl}`)}`, "_blank", "noopener");
    toast.success("Ouverture de WhatsApp…");
  };

  if (!data) return null;
  return (
    <section data-testid="badges-section" className="bg-white rounded-3xl border border-[#E4E0D5] p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Mes badges</h3>
        <span data-testid="badges-count" className="text-[12px] font-bold text-[#0A1426]">{data.obtenus}/{data.total}</span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {data.badges.map((b) => {
          const I = ICONS[b.icone] || Medal;
          return (
            <button key={b.code} data-testid={`badge-${b.code}`} onClick={() => b.obtenu && setCelebrate({ ...b, replay: true })}
              className="flex flex-col items-center gap-1" title={b.titre}>
              <span className="w-12 h-12 rounded-2xl grid place-items-center" style={b.obtenu ? { backgroundColor: `${b.couleur}22`, color: b.couleur } : { backgroundColor: "#F5F3EE", color: "#0A4A4555" }}>
                {b.obtenu ? <I size={22} /> : <Lock size={16} />}
              </span>
              <span className={`text-[9px] font-bold text-center leading-tight ${b.obtenu ? "text-[#0A1426]" : "text-[#0A1426]/40"}`}>{b.titre}</span>
            </button>
          );
        })}
      </div>

      {celebrate && (
        <div className="fixed inset-0 z-[85] bg-[#0A1426]/90 flex items-center justify-center p-6" onClick={closeCelebrate}>
          <div data-testid="badge-celebration" onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-6 w-full max-w-sm text-center flex flex-col items-center gap-3 bc-in">
            <span className="w-24 h-24 rounded-full grid place-items-center animate-bounce" style={{ backgroundColor: `${celebrate.couleur}22`, color: celebrate.couleur }}>
              {(() => { const I = ICONS[celebrate.icone] || Medal; return <I size={44} />; })()}
            </span>
            <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">{celebrate.replay ? "Badge obtenu" : "Nouveau badge !"}</div>
            <h2 className="font-[Fraunces] text-2xl font-bold text-[#0A1426]">{celebrate.titre}</h2>
            <p className="text-[15px] text-[#0A1426]/80">{lang === "ln" ? celebrate.ln : celebrate.texte}</p>
            <button data-testid="badge-listen-btn" onClick={() => speak(lang === "ln" ? celebrate.ln : `Félicitations ! ${celebrate.texte}`, lang)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F5F3EE] text-[#0A1426] font-bold text-sm"><Volume2 size={16} /> {lang === "ln" ? "Yoka" : "Réécouter"}</button>
            <img data-testid="badge-share-preview" src={`${process.env.REACT_APP_BACKEND_URL}/api/share/badge/${celebrate.code}.png?nom=${encodeURIComponent((patient?.nom || "").split(" ")[0])}${lang === "ln" ? "&ln=1" : ""}`} alt="" className="w-28 h-28 rounded-2xl border border-[#E4E0D5]" loading="lazy" />
            <Button data-testid="badge-share-btn" onClick={() => shareBadge(celebrate)} className="w-full h-12 rounded-full bg-[#25D366] hover:bg-[#1da851] text-white font-bold">
              <MessageCircle size={18} className="mr-2" /> Partager sur WhatsApp
            </Button>
            <Button data-testid="badge-close-btn" onClick={closeCelebrate} variant="outline" className="w-full h-12 rounded-full border-[#0A1426] text-[#0A1426] font-bold">Merci !</Button>
          </div>
        </div>
      )}
    </section>
  );
}
